import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { selectExpenseCategoryIdFromRoute } from 'src/app/ExpenseCategoryLabels/redux/expenseCategoryLabels.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { ExpenseCategoryLabelsBulkUploadSuccessToastMessage } from 'src/app/Toast/Toast'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BulkUploadForm, {
  BulkUploadFormFlatFields,
} from 'src/components/BulkUploadForm/BulkUploadForm'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import {
  queryExpenseCategoryLabelsBulkTemplate,
  usePostBulkExpenseCategoryLabels,
} from 'src/queries/organizations/expenseCategories.queries'
import { useRequestConfig } from 'src/queries/queries.utils'
import {
  selectAppName,
  selectDefaultLocaleFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BulkUploadExpenseLabels: React.FC = () => {
  const translate = useTranslate()

  const appName = useSelector(selectAppName)
  const locale = useSelector(selectDefaultLocaleFromState)
  const listImage = useColorOrGrayscaleImage(ImageNames.List)
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  const categoryId = useSelector(selectExpenseCategoryIdFromRoute)

  const dispatch = useDispatch()

  const [templateDownloadIsPending, setTemplateDownloadIsPending] =
    useState<boolean>(false)
  const getTemplate = (): void => {
    queryExpenseCategoryLabelsBulkTemplate(
      queryClient,
      requestConfig,
      setTemplateDownloadIsPending,
    )
  }

  const {
    mutate: postCSV,
    isPending,
    error,
  } = usePostBulkExpenseCategoryLabels()

  const onSuccessfulSubmit = (flatFields: BulkUploadFormFlatFields): void => {
    if (flatFields.csv !== undefined) {
      const formData = new FormData()
      formData.append('file', flatFields.csv, flatFields.csv.name)
      postCSV(
        { categoryId, body: formData },
        {
          onSuccess: (data) => {
            // close flyout and create toast
            dispatch(closeModal())
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <ExpenseCategoryLabelsBulkUploadSuccessToastMessage
                    count={data.count.toString()}
                  />
                ),
              }),
            )
          },
        },
      )
    }
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      <Helmet>
        <title>{`Create Expense Labels | ${appName}`}</title>
        <meta
          name="description"
          content={`Create Expense Labels page of ${appName}`}
        />
      </Helmet>
      <ActionRightFlyoutContent
        testId={'expenseLabelsCSVUploadFlyout'}
        title={translate('Create New Labels')}
        specificElements={{
          subtitle: translate('Bulk CSV Upload'),
          imageComponent: (
            <img
              css={css`
                position: absolute;
                top: -2px;
                right: -6px;
                transform: rotate(-15deg);

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
              src={listImage}
              alt={''}
            />
          ),
        }}
      >
        <BulkUploadForm
          onSuccessfulSubmit={onSuccessfulSubmit}
          postingBulkUpload={isPending}
          uploadError={
            error?.error
              ? translateSingleString(
                  locale,
                  'There was an error creating labels. Please review your file to ensure there are no duplicates or blanks.',
                )
              : undefined
          }
          title={translate('Import CSV')}
          subtitle={translate(
            'Use the available template to import labels to this expense category. Maximum file size: 10 MB.',
          )}
          buttonText={translate('Confirm and Import Labels')}
          buttonTextWithRowCount={'Confirm and Import ${rowCount} Labels'}
          templateDownloadOptions={[
            {
              key: 'expenseCategoryLabelsTemplate',
              labelSrc: 'string',
              onClick: getTemplate,
              testId: 'expenseCategoryLabelsTemplate',
            },
          ]}
          loadingTemplate={templateDownloadIsPending}
        />
      </ActionRightFlyoutContent>
    </RightFlyout>
  )
}

export default BulkUploadExpenseLabels
